import { useRef, useCallback } from 'react';
import axios from '../services/api';
import { useI18n } from '../contexts/I18nContext';
import { handleUnifiedError, classifyError, ERROR_TYPES } from '../utils/unifiedErrorHandler';

/**
 * 通用轮询Hook，用于处理图片生成状态轮询
 * @param {Object} options 配置选项
 * @param {number} options.maxAttempts 最大轮询次数，默认60次
 * @param {number} options.maxConsecutiveErrors 最大连续错误次数，默认5次
 * @param {number} options.pollInterval 轮询间隔（毫秒）
 * @param {number} options.initialDelay 初始延迟（毫秒）
 * @param {number} options.progressUpdateInterval 进度更新间隔（毫秒），默认500ms
 * @param {Function} options.onSuccess 成功回调
 * @param {Function} options.onFailed 失败回调
 * @param {Function} options.onTimeout 超时回调
 * @param {Function} options.onError 错误回调
 * @returns {Object} 包含startPolling和stopPolling方法的对象
 */
export const usePolling = (options = {}) => {
  const { t } = useI18n();
  const timeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  const {
    maxAttempts = 60,
    maxConsecutiveErrors = 5,
    pollInterval = 3000,
    initialDelay = 1000,
    progressUpdateInterval = 500,
    onSuccess,
    onFailed,
    onTimeout,
    onError
  } = options;

  /**
   * 处理轮询错误 - 使用统一错误处理系统
   * @param {Error} error 错误对象
   * @param {number} consecutiveErrors 连续错误次数
   * @param {number} attempts 当前尝试次数
   * @returns {Object} 包含错误信息和是否应该继续轮询的标志
   */
  const handlePollingError = useCallback((error, consecutiveErrors, attempts) => {
    console.error('Polling error:', error);
    
    // 使用统一错误处理系统分类错误
    const { type, severity } = classifyError(error);
    
    let errorMessage = t('polling.queryStatusFailed');
    let shouldContinue = true;
    let delay = pollInterval;
    
    if (error.response) {
      // 服务器响应错误
      const status = error.response.status;
      if (status === 404) {
        errorMessage = t('polling.taskNotFound');
        shouldContinue = false;
      } else if (status === 401) {
        errorMessage = t('polling.authenticationFailed');
        shouldContinue = false;
      } else if (status >= 500) {
        errorMessage = t('polling.serverInternalError');
      } else if (status === 429) {
        errorMessage = t('polling.requestTooFrequent');
        delay = pollInterval * 2; // 增加延迟
      }
    } else if (error.request) {
      // 网络错误
      errorMessage = t('polling.networkConnectionFailed');
    } else {
      // 其他错误
      errorMessage = error.message || t('polling.unknownError');
    }
    
    // 检查是否应该停止轮询
    const shouldStop = consecutiveErrors >= maxConsecutiveErrors || 
                      !shouldContinue || 
                      attempts >= maxAttempts;
    
    // 错误后增加延迟
    if (consecutiveErrors > 2) {
      delay = Math.min(delay * 1.5, 15000); // 最大延迟15秒
    }
    
    return {
      errorMessage,
      shouldStop,
      delay,
      type,
      severity
    };
  }, [maxConsecutiveErrors, maxAttempts, pollInterval, t]);

  /**
   * 解析错误消息 - 使用统一错误处理系统
   * @param {string} errorMessage 原始错误消息
   * @returns {Object|string} 解析后的错误对象或字符串
   */
  const parseErrorMessage = useCallback((errorMessage) => {
    if (!errorMessage) return { type: ERROR_TYPES.UNKNOWN, message: t('polling.generationFailed') };
    
    // 使用统一错误分类器
    const { type, severity } = classifyError(errorMessage);
    
    try {
      // 尝试解析JSON格式的错误消息
      const parsed = JSON.parse(errorMessage);
      if (parsed.type && parsed.message) {
        // 使用统一错误处理系统重新分类
        const { type: unifiedType } = classifyError(parsed);
        return {
          type: unifiedType,
          message: parsed.message,
          severity,
          originalType: parsed.type
        };
      }
    } catch (e) {
      // 不是JSON格式，继续使用统一分类结果
    }
    
    // 返回统一分类的结果
    return {
      type,
      severity,
      message: errorMessage,
      originalMessage: errorMessage
    };
  }, [t]);

  /**
   * 显示错误提示 - 使用统一错误处理系统
   * @param {string|Object} errorInfo 错误信息
   */
  const showErrorToast = useCallback((errorInfo) => {
    // 使用统一错误处理系统显示错误
    handleUnifiedError(errorInfo, t, {
      showToast: true,
      duration: 6000
    });
  }, [t]);

  /**
   * 开始轮询
   * @param {string} generationId 生成任务ID
   * @param {Function} progressCallback 进度更新回调
   */
  const startPolling = useCallback((generationId, progressCallback) => {
    let attempts = 0;
    let consecutiveErrors = 0;
    let currentProgress = 0; // 当前进度跟踪
    let isCompleted = false; // 完成状态标志

    // 启动独立的进度更新定时器
    const startProgressUpdater = () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      progressIntervalRef.current = setInterval(() => {
        if (!isCompleted && currentProgress < 90) {
          // 每500ms增加1-2%的进度，确保不超过90%
          const increment = Math.random() * 1 + 1; // 1-2%的随机增量
          currentProgress = Math.min(currentProgress + increment, 90);
          if (progressCallback) {
            progressCallback(Math.floor(currentProgress));
          }
        }
      }, progressUpdateInterval);
    };

    // 停止进度更新定时器
    const stopProgressUpdater = () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };

    const poll = async () => {
      try {
        const response = await axios.get(`/images/${generationId}`);
        const generation = response.data;
        
        // 重置连续错误计数
        consecutiveErrors = 0;
        
        if (generation.status === 'COMPLETED' && generation.imageUrl) {
          // 生成完成
          isCompleted = true;
          stopProgressUpdater();
          if (progressCallback) progressCallback(100);
          if (onSuccess) {
            setTimeout(() => onSuccess(generation), 500);
          }
        } else if (generation.status === 'FAILED') {
          // 生成失败
          isCompleted = true;
          stopProgressUpdater();
          if (progressCallback) progressCallback(0); // 重置进度条到0
          const parsedError = parseErrorMessage(generation.errorMessage);
          showErrorToast(parsedError);
          if (onFailed) onFailed(parsedError);
        } else if (generation.status === 'processing' || generation.status === 'PROCESSING') {
          // 先递增attempts计数
          attempts++;
          
          // 继续轮询 - 进度由独立定时器更新
          if (attempts < maxAttempts) {
            timeoutRef.current = setTimeout(poll, pollInterval);
          } else {
            // 超时
            isCompleted = true;
            stopProgressUpdater();
            const timeoutMessage = t('polling.timeout');
            showErrorToast(timeoutMessage);
            if (onTimeout) onTimeout(timeoutMessage);
          }
        } else {
          // 先递增attempts计数
          attempts++;
          
          // 未知状态，记录警告并继续轮询
          console.warn(`Unknown generation status: ${generation.status}`);
          
          if (attempts < maxAttempts) {
            timeoutRef.current = setTimeout(poll, pollInterval);
          } else {
            isCompleted = true;
            stopProgressUpdater();
            const timeoutMessage = t('polling.timeout');
            showErrorToast(timeoutMessage);
            if (onTimeout) onTimeout(timeoutMessage);
          }
        }
      } catch (error) {
        consecutiveErrors++;
        const errorResult = handlePollingError(error, consecutiveErrors, attempts);
        
        if (errorResult.shouldStop) {
          isCompleted = true;
          stopProgressUpdater();
          showErrorToast(errorResult.errorMessage);
          if (onError) onError(error);
        } else {
          // 继续轮询
          attempts++;
          timeoutRef.current = setTimeout(poll, errorResult.delay);
        }
      }
    };

    // 启动进度更新器
    startProgressUpdater();
    
    // 延迟启动轮询
    timeoutRef.current = setTimeout(poll, initialDelay);
  }, [
    maxAttempts, 
    pollInterval, 
    initialDelay, 
    progressUpdateInterval,
    onSuccess, 
    onFailed, 
    onTimeout, 
    onError,
    handlePollingError,
    parseErrorMessage,
    showErrorToast,
    t
  ]);

  /**
   * 停止轮询
   */
  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  return {
    startPolling,
    stopPolling
  };
};

export default usePolling;
